import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Pencil, Eye, Trash2, X, Plus, AlertTriangle, ArrowLeft, CheckCircle2, ShieldAlert, Image as ImageIcon, ShieldCheck, Upload } from "lucide-react";
import api, { apiErr } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/api") ? BACKEND + u : u);

const STATUSES = ["Idea", "Researching", "Draft", "In Review", "Approved", "Published"];
const PREVIEW_MD = {
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-pti-red underline" />,
  hr: () => <hr className="my-7 border-0 border-t-2 border-pti-red" />,
  h2: ({ node, ...props }) => <h2 {...props} className="font-display text-2xl font-bold text-pti-black mt-8 mb-2" />,
  h3: ({ node, ...props }) => <h3 {...props} className="font-display text-lg font-bold text-pti-black mt-6 mb-2" />,
};

const STATUS_STYLE = {
  Idea: "bg-gray-100 text-gray-600", Researching: "bg-amber-100 text-amber-700",
  Draft: "bg-blue-100 text-blue-700", "In Review": "bg-purple-100 text-purple-700",
  Approved: "bg-emerald-100 text-emerald-700", Published: "bg-pti-red text-white",
};
const CATEGORIES = ["Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Innovation & Startups", "Lifestyle & Culture", "Inspiration & People"];
const CHECKS = [["sources_reviewed", "Sources reviewed"], ["headline_accurate", "Headline accurate"], ["dates_verified", "Dates verified"], ["seo_complete", "SEO fields complete"], ["image_attached", "Featured image attached"], ["alt_complete", "Alt text complete"], ["no_unsupported_claims", "No unsupported claims"], ["ready_for_review", "Ready for human review"]];

const StatusBadge = ({ s }) => <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 ${STATUS_STYLE[s] || "bg-gray-100"}`}>{s}</span>;

export default function Daily3Desk() {
  const [drafts, setDrafts] = useState([]);
  const [aiAvailable, setAi] = useState(true);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/editorial/daily3"); setDrafts(r.data.drafts); setAi(r.data.ai_available); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (withSources = false) => {
    if (generating) return;
    setGenerating(true);
    if (!withSources) {
      toast.info("Generating 3 drafts… this takes ~20s.");
      try { await api.post("/admin/editorial/generate"); toast.success("3 drafts created (status: Draft). Add sources before advancing."); await load(); }
      catch (e) { toast.error(apiErr(e)); } finally { setGenerating(false); }
      return;
    }
    // Sourced generation runs in the background (web research ~60-90s) — kick it off, then poll.
    toast.info("Researching the web & drafting with real sources… this can take up to 90s.");
    const baseline = drafts.length;
    try {
      await api.post("/admin/editorial/generate?with_sources=true");
      let done = false;
      for (let i = 0; i < 12 && !done; i++) {
        await new Promise((r) => setTimeout(r, 10000));
        try {
          const r = await api.get("/admin/editorial/daily3");
          if ((r.data.drafts || []).length >= baseline + 3) {
            setDrafts(r.data.drafts); setAi(r.data.ai_available);
            const withSrc = r.data.drafts.slice(0, 3).filter((d) => (d.source_count || 0) >= 2).length;
            toast.success(`3 sourced drafts ready — ${withSrc} have 2+ verified sources. Review before publishing.`);
            done = true;
          }
        } catch (_e) { /* keep polling */ }
      }
      if (!done) { toast.message("Still researching — refreshing what's ready so far."); await load(); }
    } catch (e) { toast.error(apiErr(e)); } finally { setGenerating(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this draft?")) return; try { await api.delete(`/admin/editorial/${id}`); toast.success("Deleted"); setDrafts((p) => p.filter((d) => d.id !== id)); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="min-h-screen bg-white text-pti-black" data-testid="daily3-desk">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-pti-black mb-4" data-testid="daily3-back"><ArrowLeft size={15} /> Back to Admin</Link>
        <div className="flex items-start justify-between flex-wrap gap-4 border-b-4 border-pti-red pb-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[3px] text-pti-red">Internal · Editorial</div>
            <h1 className="font-display text-4xl font-black mt-1">Daily 3 Editorial Desk</h1>
            <p className="text-sm text-gray-500 mt-1">Generate research-ready drafts. Nothing publishes automatically — every story requires sources and a manual confirmation.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={() => generate(false)} disabled={generating} data-testid="daily3-generate" className="flex items-center justify-center gap-2 border-2 border-pti-black text-pti-black font-bold text-sm px-5 py-3 hover:bg-pti-black hover:text-white transition-colors disabled:opacity-60">
              <Sparkles size={16} /> {generating ? "Generating…" : "Generate Today's 3 Drafts"}
            </button>
            <button onClick={() => generate(true)} disabled={generating} data-testid="daily3-generate-sources" className="flex items-center justify-center gap-2 bg-pti-red text-white font-bold text-sm px-5 py-3 hover:bg-pti-black transition-colors disabled:opacity-60">
              <ShieldCheck size={16} /> {generating ? "Researching…" : "Generate With Sources"}
            </button>
          </div>
        </div>

        {!aiAvailable && (
          <div className="mt-5 flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3" data-testid="daily3-ai-warning">
            <ShieldAlert size={16} /> AI connection required — drafts will be created as empty "Idea" items until an AI key is configured.
          </div>
        )}

        <h2 className="font-display text-lg font-bold mt-8 mb-4">Today's Daily 3</h2>
        {loading ? <p className="text-gray-400 text-sm py-10 text-center">Loading…</p>
          : drafts.length === 0 ? <p className="text-gray-400 text-sm py-14 text-center border border-dashed border-gray-200" data-testid="daily3-empty">No drafts yet. Click "Generate Today's 3 Drafts" to start.</p>
          : (
          <div className="grid md:grid-cols-3 gap-6">
            {drafts.map((d) => (
              <div key={d.id} className="border border-gray-200 flex flex-col" data-testid={`daily3-card-${d.id}`}>
                <img src={imgSrc(d.featured_image)} alt={d.image_alt || ""} className="w-full aspect-video object-cover bg-gray-100" />
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-pti-red">{d.category}</span>
                    <StatusBadge s={d.status} />
                  </div>
                  <h3 className="font-display text-lg font-bold leading-tight mt-2">{d.headline}</h3>
                  {d.source_count < 2 && <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-amber-700"><AlertTriangle size={12} /> Needs {2 - d.source_count} more source{2 - d.source_count === 1 ? "" : "s"}</div>}
                  <div className="mt-3 grid grid-cols-2 gap-1 text-[11px] text-gray-500">
                    <div>{d.word_count} words</div><div className="text-right">{d.source_count} sources</div>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1">Updated {(d.updated_at || "").slice(0, 10)}</div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button onClick={() => setEditing(d.id)} data-testid={`daily3-edit-${d.id}`} className="flex-1 flex items-center justify-center gap-1 text-xs font-bold border border-pti-black py-1.5 hover:bg-gray-50"><Pencil size={12} /> Edit</button>
                    <button onClick={() => setPreviewing(d)} data-testid={`daily3-preview-${d.id}`} className="flex items-center justify-center gap-1 text-xs font-bold border border-gray-300 px-3 py-1.5 hover:border-pti-black"><Eye size={12} /></button>
                    <button onClick={() => del(d.id)} data-testid={`daily3-delete-${d.id}`} className="flex items-center justify-center text-gray-400 hover:text-pti-red px-2 py-1.5"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <EditDrawer id={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {previewing && <PreviewModal draft={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function PreviewModal({ draft, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(17,17,17,0.6)" }} onClick={onClose} data-testid="daily3-preview-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white"><h3 className="font-display font-black">Preview</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-6">
          <span className="text-[11px] uppercase font-bold text-pti-red">{draft.category}</span>
          <h1 className="font-display text-3xl font-black mt-1">{draft.headline}</h1>
          <img src={imgSrc(draft.featured_image)} alt={draft.image_alt} className="w-full aspect-video object-cover mt-4" />
          <div className="prose max-w-none mt-4 text-gray-800"><ReactMarkdown remarkPlugins={[remarkGfm]} components={PREVIEW_MD}>{draft.body || "_No body yet._"}</ReactMarkdown></div>
        </div>
      </div>
    </div>
  );
}

function EditDrawer({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [src, setSrc] = useState({ title: "", publication: "", url: "", date: "", notes: "" });
  const [confirmPub, setConfirmPub] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const fileRef = useRef(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const fetchD = async () => { try { const r = await api.get(`/admin/editorial/${id}`); setD(r.data); } catch (e) { toast.error(apiErr(e)); onClose(); } };
  useEffect(() => { fetchD(); }, [id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const body = { headline: d.headline, category: d.category, excerpt: d.excerpt, body: d.body, tags: d.tags, meta_title: d.meta_title, meta_description: d.meta_description, image_prompt: d.image_prompt, image_alt: d.image_alt, research_brief: d.research_brief, editorial_caution: d.editorial_caution, checklist: d.checklist };
      const r = await api.put(`/admin/editorial/${id}`, body); setD(r.data); toast.success("Saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const addSource = async () => {
    if (!src.title) return toast.error("Source title required");
    try { const r = await api.post(`/admin/editorial/${id}/sources`, src); setD(r.data); setSrc({ title: "", publication: "", url: "", date: "", notes: "" }); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delSource = async (sid) => { try { const r = await api.delete(`/admin/editorial/${id}/sources/${sid}`); setD(r.data); onSaved(); } catch (e) { toast.error(apiErr(e)); } };
  const genImage = async () => {
    if (imgBusy) return; setImgBusy(true); toast.info("Generating on-brand image… ~15s.");
    try { const r = await api.post(`/admin/editorial/${id}/image`); setD((p) => ({ ...p, featured_image: r.data.featured_image, checklist: { ...p.checklist, image_attached: true } })); toast.success("Featured image generated."); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setImgBusy(false); }
  };
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) return toast.error("Upload a PNG, JPG, WEBP or GIF image.");
    setUpBusy(true); toast.info("Uploading image…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/admin/editorial/${id}/image/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setD((p) => ({ ...p, featured_image: r.data.featured_image, checklist: { ...p.checklist, image_attached: true } }));
      toast.success("Image uploaded and attached."); onSaved();
    } catch (err) { toast.error(apiErr(err)); } finally { setUpBusy(false); }
  };
  const checkSources = async () => {
    if (checkBusy) return; setCheckBusy(true);
    try { const r = await api.post(`/admin/editorial/${id}/check-sources`); setD(r.data); const n = (r.data.sources || []).filter((s) => (s.flags || []).length).length; toast[n ? "warning" : "success"](n ? `${n} source${n === 1 ? "" : "s"} flagged — review below.` : "All sources look healthy."); }
    catch (e) { toast.error(apiErr(e)); } finally { setCheckBusy(false); }
  };
  const changeStatus = async (status) => {
    try { const r = await api.post(`/admin/editorial/${id}/status?status=${encodeURIComponent(status)}`); setD(r.data); toast.success(`Status: ${status}`); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const publish = async () => {
    try { const r = await api.post(`/admin/editorial/${id}/publish?confirm=true`); toast.success("Published to PictureThisInk."); setConfirmPub(false); await fetchD(); onSaved();
      if (r.data.slug) window.open(`/article/${r.data.slug}`, "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const INP = "w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pti-red";
  const LBL = "text-xs font-bold uppercase tracking-wide text-gray-500";

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" style={{ backgroundColor: "rgba(17,17,17,0.5)" }} onClick={onClose} data-testid="daily3-edit-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!d ? <p className="p-10 text-center text-gray-400">Loading…</p> : (
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between sticky -top-6 bg-white pt-1 pb-3 border-b z-10 -mx-6 px-6">
              <div className="flex items-center gap-3"><h2 className="font-display text-xl font-black">Edit Draft</h2><StatusBadge s={d.status} /></div>
              <div className="flex items-center gap-2">
                <button onClick={save} disabled={saving} data-testid="daily3-save" className="bg-pti-red text-white text-sm font-bold px-4 py-2 hover:bg-pti-black disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
                <button onClick={onClose}><X size={20} /></button>
              </div>
            </div>

            {d.editorial_caution && <div className="flex gap-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2" data-testid="daily3-caution"><AlertTriangle size={16} className="shrink-0 mt-0.5" /><div><b>Editorial caution:</b> {d.editorial_caution}</div></div>}

            <div><span className={LBL}>Headline</span><input value={d.headline || ""} onChange={(e) => set("headline", e.target.value)} data-testid="daily3-f-headline" className={INP} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={LBL}>Category</span><select value={d.category} onChange={(e) => set("category", e.target.value)} className={INP}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><span className={LBL}>Tags (comma sep)</span><input value={Array.isArray(d.tags) ? d.tags.join(", ") : ""} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={INP} /></div>
            </div>
            <div><span className={LBL}>Excerpt</span><textarea value={d.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={INP} /></div>
            <div><span className={LBL}>Body ({(d.body || "").split(/\s+/).filter(Boolean).length} words)</span><textarea value={d.body || ""} onChange={(e) => set("body", e.target.value)} rows={10} data-testid="daily3-f-body" className={INP} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={LBL}>Meta title</span><input value={d.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} className={INP} /></div>
              <div><span className={LBL}>Meta description</span><input value={d.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className={INP} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={LBL}>Featured image prompt</span><textarea value={d.image_prompt || ""} onChange={(e) => set("image_prompt", e.target.value)} rows={2} className={INP} /></div>
              <div><span className={LBL}>Image alt text</span><textarea value={d.image_alt || ""} onChange={(e) => set("image_alt", e.target.value)} rows={2} className={INP} /></div>
            </div>
            <div className="flex items-center gap-3 border border-gray-200 p-3">
              <img src={imgSrc(d.featured_image)} alt="" className="w-28 aspect-video object-cover bg-gray-100 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Featured image is a branded placeholder until you generate or upload a real one.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={genImage} disabled={imgBusy || upBusy} data-testid="daily3-gen-image" className="flex items-center gap-1.5 text-sm font-bold border border-pti-red text-pti-red px-3 py-1.5 hover:bg-pti-red hover:text-white transition-colors disabled:opacity-60"><ImageIcon size={13} /> {imgBusy ? "Generating…" : "Generate image (AI)"}</button>
                  <button onClick={() => fileRef.current?.click()} disabled={imgBusy || upBusy} data-testid="daily3-upload-image" className="flex items-center gap-1.5 text-sm font-bold border border-pti-black text-pti-black px-3 py-1.5 hover:bg-pti-black hover:text-white transition-colors disabled:opacity-60"><Upload size={13} /> {upBusy ? "Uploading…" : "Upload image (PNG)"}</button>
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif" onChange={uploadImage} className="hidden" data-testid="daily3-image-file-input" />
                </div>
              </div>
            </div>
            <div><span className={LBL}>Research brief</span><textarea value={d.research_brief || ""} onChange={(e) => set("research_brief", e.target.value)} rows={2} className={INP} /></div>

            {/* Sources */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2"><span className={LBL}>Sources</span>{(d.sources?.length || 0) < 2 && <span className="text-[11px] font-bold text-amber-700 flex items-center gap-1"><AlertTriangle size={11} /> At least 2 required before advancing</span>}</div>
                <button onClick={checkSources} disabled={checkBusy || (d.sources?.length || 0) === 0} data-testid="daily3-check-sources" className="flex items-center gap-1.5 text-xs font-bold border border-gray-300 px-3 py-1.5 hover:border-pti-black disabled:opacity-50"><ShieldCheck size={13} /> {checkBusy ? "Checking…" : "Check sources"}</button>
              </div>
              <div className="space-y-2 mt-2">
                {(d.sources || []).map((s) => (
                  <div key={s.id} className={`flex items-start justify-between gap-2 border p-2 text-sm ${(s.flags || []).length ? "border-amber-300 bg-amber-50" : "border-gray-200"}`} data-testid={`daily3-source-${s.id}`}>
                    <div><div className="font-bold">{s.title}</div><div className="text-[11px] text-gray-500">{s.publication} {s.date && `· ${s.date}`} {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-pti-red underline ml-1">link</a>}</div>{s.notes && <div className="text-[11px] text-gray-400 mt-0.5">{s.notes}</div>}
                      {(s.flags || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{s.flags.map((f) => <span key={f} className="text-[10px] font-bold uppercase bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-sm">{{ missing_url: "No URL", duplicate: "Duplicate", unreachable: "Broken link", outdated: "Outdated" }[f] || f}</span>)}</div>}
                    </div>
                    <button onClick={() => delSource(s.id)} className="text-gray-400 hover:text-pti-red"><Trash2 size={13} /></button>
                  </div>
                ))}
                {(d.sources || []).length === 0 && <p className="text-xs text-gray-400">No sources yet.</p>}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <input placeholder="Source title *" value={src.title} onChange={(e) => setSrc({ ...src, title: e.target.value })} data-testid="daily3-src-title" className={INP} />
                <input placeholder="Publication / org" value={src.publication} onChange={(e) => setSrc({ ...src, publication: e.target.value })} className={INP} />
                <input placeholder="URL" value={src.url} onChange={(e) => setSrc({ ...src, url: e.target.value })} className={INP} />
                <input placeholder="Date published/accessed" value={src.date} onChange={(e) => setSrc({ ...src, date: e.target.value })} className={INP} />
                <input placeholder="What fact it supports" value={src.notes} onChange={(e) => setSrc({ ...src, notes: e.target.value })} className={`${INP} col-span-2`} />
              </div>
              <button onClick={addSource} data-testid="daily3-add-source" className="mt-2 flex items-center gap-1 text-sm font-bold border border-pti-black px-3 py-1.5 hover:bg-gray-50"><Plus size={13} /> Add source</button>
            </div>

            {/* Checklist */}
            <div className="border-t pt-4">
              <span className={LBL}>Pre-review checklist</span>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {CHECKS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!d.checklist?.[k]} onChange={(e) => set("checklist", { ...d.checklist, [k]: e.target.checked })} data-testid={`daily3-check-${k}`} className="accent-pti-red w-4 h-4" /> {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Status + publish */}
            <div className="border-t pt-4">
              <span className={LBL}>Status</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {STATUSES.filter((s) => s !== "Published").map((s) => (
                  <button key={s} onClick={() => changeStatus(s)} data-testid={`daily3-status-${s.replace(/\s/g, "-")}`} className={`text-xs font-bold px-3 py-1.5 border ${d.status === s ? "bg-pti-black text-white border-pti-black" : "border-gray-300 hover:border-pti-black"}`}>{s}</button>
                ))}
              </div>
              <button onClick={() => setConfirmPub(true)} data-testid="daily3-open-publish" className="mt-4 w-full flex items-center justify-center gap-2 bg-pti-red text-white font-bold text-sm py-3 hover:bg-pti-black transition-colors">Publish…</button>
            </div>

            {d.status === "Published" && d.published_article_id && <p className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 size={15} /> Published on {(d.published_at || "").slice(0, 10)}</p>}

            {/* Activity log */}
            <div className="border-t pt-4">
              <span className={LBL}>Editorial history</span>
              <ul className="mt-2 space-y-1 text-[11px] text-gray-500" data-testid="daily3-activity">
                {[...(d.activity_log || [])].reverse().map((a, i) => <li key={i}><b className="text-gray-700">{a.action}</b> {a.detail && `— ${a.detail}`} <span className="text-gray-400">· {(a.at || "").replace("T", " ").slice(0, 16)}</span></li>)}
              </ul>
            </div>
          </div>
        )}
      </div>

      {confirmPub && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(17,17,17,0.7)" }} onClick={() => setConfirmPub(false)} data-testid="daily3-publish-confirm">
          <div className="bg-white w-full max-w-md shadow-2xl border-t-4 border-t-pti-red p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-pti-red"><ShieldAlert size={20} /><h3 className="font-display text-xl font-black">Confirm publish</h3></div>
            <p className="text-sm text-gray-700 mt-3">Publishing makes this article public on PictureThisInk. Confirm that all facts, links, image rights, SEO fields, and disclosures have been reviewed.</p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmPub(false)} className="px-4 py-2 text-sm font-bold border border-gray-300 hover:border-pti-black">Cancel</button>
              <button onClick={publish} data-testid="daily3-confirm-publish" className="px-5 py-2 text-sm font-bold bg-pti-red text-white hover:bg-pti-black">Publish now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
